import { Vector3, Mesh, MeshPhongMaterial, Color, NoBlending } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { VRObject3D } from './VRObject3D';
import { youtubePlaneGeometry } from './helpers/texturesAndGeometries';

/**
 * Parses iframe value - accepts full iframe HTML or URL string.
 * @param {string} iframeValue - Full iframe HTML or URL
 * @returns {{ html: string|null, src: string }}
 */
function parseIframeValue(iframeValue) {
    if (!iframeValue || typeof iframeValue !== 'string') return { html: null, src: '' };
    const trimmed = iframeValue.trim();
    if (trimmed.toLowerCase().startsWith('<iframe')) {
        return { html: trimmed, src: '' };
    }
    return { html: null, src: trimmed };
}

/**
 * Appends a one-time query param to bypass iframe cache.
 * @param {string} url
 * @returns {string}
 */
function withCacheBreaker(url) {
    if (!url || typeof url !== 'string') return '';
    const [base, hash = ''] = url.split('#');
    const separator = base.includes('?') ? '&' : '?';
    const breaker = `cb=${Date.now()}_${Math.random().toString(36).slice(2)}`;
    return `${base}${separator}${breaker}${hash ? `#${hash}` : ''}`;
}

class VRIframeAgent extends VRObject3D {

    constructor(camera, iframeValue, x, y, z, model, dbConfig = {}) {

        super();
        this.camera = camera;

        const div = document.createElement('div');
        div.setAttribute("class", "iframe_element");
        div.setAttribute("data-css3d-interactive", "true");

        // Override .iframe_element defaults when enabled in dbConfig
        if (dbConfig.iframe_width_enabled && dbConfig.iframe_width) {
            div.style.width = dbConfig.iframe_width;
        }
        if (dbConfig.iframe_height_enabled && dbConfig.iframe_height) {
            div.style.height = dbConfig.iframe_height;
        }

        const { html, src } = parseIframeValue(iframeValue);
        const IFRAME_ALLOW = "camera; microphone; autoplay; fullscreen";

        const loadIframe = () => {
            startBtn && startBtn.remove();
            if (div.dataset.loaded === 'true') return;
            div.dataset.loaded = 'true';

            if (html) {
                div.innerHTML = html;
                const iframeEl = div.querySelector('iframe');
                if (iframeEl) {
                    iframeEl.setAttribute('allow', IFRAME_ALLOW);
                    const iframeSrc = iframeEl.getAttribute('src');
                    if (iframeSrc) iframeEl.setAttribute('src', withCacheBreaker(iframeSrc));
                }
            } else {
                const iframe = document.createElement('iframe');
                iframe.setAttribute('allowfullscreen', true);
                iframe.setAttribute('allow', IFRAME_ALLOW);
                iframe.setAttribute('title', 'iframe');
                iframe.src = withCacheBreaker(src || '');
                div.appendChild(iframe);
            }
        };

        const startBtn = document.createElement('button');
        startBtn.setAttribute('class', 'iframe_element__start-btn');
        startBtn.textContent = 'Start Conversation';
        startBtn.type = 'button';
        startBtn.addEventListener('click', () => {
            loadIframe();
            startBtn.remove();
        }, { once: true });
        div.appendChild(startBtn);

        document.addEventListener('click', () => loadIframe(), { once: true });

        const material = new MeshPhongMaterial({
            opacity: 0,
            transparent: true,
            color: new Color(0x000000),
            blending: NoBlending
        })

        this.mesh = new Mesh(youtubePlaneGeometry, material);

        const object = new CSS3DObject(div);
        if (camera !== null) {
            object.position.copy(this.camera.position);
            object.rotation.copy(this.camera.rotation);
            object.translateZ(-540);
        } else {
            object.position.copy(new Vector3(x, y, z));
        }

        this.mesh.dbConfig = {};
        if (!model.editMode) {
            this.mesh.visible = false;
        }

        object.add(this.mesh);
        object.mesh = this.mesh;

        object.type = 'iframeAgent';
        object.name = 'iframeAgent';

        return object;
    }

}

export { VRIframeAgent };
